/* ==========================================================================
   we-pdp-pricing.js - New PDP Pricing & Purchase Mode.
   --------------------------------------------------------------------------
   Owns the "how much does this cost" half of the buy-box: the one-time
   price, the Subscribe & Save card (percent/price/savings/per-serving) and
   the delivery-frequency <select>, all driven by real Shopify data - never
   a hand-rolled price table or a fixed subscribe rate.

   Data source: the SAME [data-wep-variant-index] JSON assets/we-pdp-
   variants.js already owns (we-pdp-buybox.liquid now adds a "pricing" key
   per variant - the variant's own price/per-serving, plus one entry per
   real selling plan, id/price/compareAt/perServing/savings/percent, each
   pre-formatted server-side via Liquid's `money` filter so it always
   matches the shop's actual currency/Markets context). This file never
   does currency math and never assumes a "$" prefix - it only ever swaps
   in one of those pre-formatted strings, and only ever computes plain,
   currency-independent things (which plan is selected, whether a string
   is blank).

   This is a read-only second reader of that JSON, exactly like Pack
   merchandising already reads the same payload's "merch" key - not a
   second variant resolver. Variant identity itself is still owned
   entirely by we-pdp-variants.js: this file only listens for its
   "wep:variant-change" event (never resolves a variant itself) and, for
   the very first paint before any click has happened, reads the variant
   id the server already rendered as selected (we-pdp-buybox.liquid's own
   [data-wep-initial-variant-id]) - the same "trust the server-rendered
   initial state" approach the option groups themselves already rely on.

   Split out of we-pdp.js for the same reason Gallery and Variants were:
   a self-contained responsibility with its own natural event boundary
   (listen for "wep:variant-change", own delegated clicks/change, render
   into its own data-wep-out targets), which also keeps we-pdp.js itself
   within the project's 400-500 line guideline as more sections come
   online. Own idempotent bind (window.WePdpPricing), own delegated
   listeners - same constraints as the other we-pdp-*.js files.
   ========================================================================== */
(function () {
  'use strict';

  if (window.WePdpPricing && window.WePdpPricing.bound) {
    window.WePdpPricing.refresh();
    return;
  }

  function qs(sel, ctx) {
    return (ctx || document).querySelector(sel);
  }

  function qsa(sel, ctx) {
    return Array.prototype.slice.call((ctx || document).querySelectorAll(sel));
  }

  function setText(sel, value) {
    var el = qs(sel);
    if (el) el.textContent = value;
  }

  /* ------------------------------------------------------------------------
     Variant data
     ------------------------------------------------------------------------
     Keyed by variant id -> {options, pricing}. Loaded once and refreshed
     on shopify:section:load, same as we-pdp-variants.js's own index.
     ------------------------------------------------------------------------ */
  var variantsById = {};

  function loadVariants() {
    variantsById = {};
    var script = qs('[data-wep-variant-index]');
    if (!script) return;
    var variants;
    try {
      variants = JSON.parse(script.textContent) || [];
    } catch (e) {
      variants = [];
    }
    variants.forEach(function (v) {
      variantsById[v.id] = { options: v.options, available: v.available, qty: v.qty, pricing: v.pricing };
    });
  }

  function initialVariantId() {
    var root = qs('[data-wep-initial-variant-id]');
    var id = root ? parseInt(root.dataset.wepInitialVariantId, 10) : NaN;
    return isNaN(id) ? null : id;
  }

  function initialMode() {
    var modes = qs('[data-wep-modes]');
    return (modes && modes.dataset.wepSelected === 'once') ? 'once' : 'sub';
  }

  function initialPlanId() {
    var select = qs('[data-wep-freq]');
    return select ? select.value : null;
  }

  var state = {
    variantId: initialVariantId(),
    mode: initialMode(),
    planId: initialPlanId()
  };

  /* ------------------------------------------------------------------------
     Purchase-mode rendering
     ------------------------------------------------------------------------ */
  function findPlan(pricing, planId) {
    if (!pricing || !pricing.plans || !pricing.plans.length) return null;
    for (var i = 0; i < pricing.plans.length; i++) {
      if (String(pricing.plans[i].id) === String(planId)) return pricing.plans[i];
    }
    return pricing.plans[0];
  }

  /* ------------------------------------------------------------------------
     Quantity-aware CTA price (fix)
     ------------------------------------------------------------------------
     The displayed price previously never changed when the shopper bought
     more than one (assets/we-pdp-cart.js's own quantity was never read
     here). formatMoney() is the one, isolated place this file does any
     currency math, and it reuses this shop's own money-formatting - never
     a hand-rolled "$" + number: window.money_format is the same format
     string every price on this page is already rendered against (set
     site-wide in snippets/global-script.liquid), and window.Shopify.
     formatMoney is the theme's own existing global helper (assets/global.js,
     loaded everywhere) - not a second formatter. Its output carries the
     shop's own `<span class="money">` wrapper (same reason Liquid's
     `money | strip_html` pair exists elsewhere in this file's payload), so
     it is stripped here for the same textContent-safety reason.
     ------------------------------------------------------------------------ */
  function formatMoney(cents) {
    if (typeof cents !== 'number' || isNaN(cents)) return null;
    if (!window.Shopify || typeof window.Shopify.formatMoney !== 'function') return null;
    return String(window.Shopify.formatMoney(cents, window.money_format)).replace(/<[^>]*>/g, '');
  }

  function currentQuantity() {
    var qty = (window.WePdpCart && typeof window.WePdpCart.getQuantity === 'function')
      ? window.WePdpCart.getQuantity()
      : 1;
    return (typeof qty === 'number' && qty > 0) ? qty : 1;
  }

  /* At quantity 1 this returns the page's original pre-formatted per-unit
     string untouched - byte-identical to before this fix. Only quantities
     above 1 (the case that was never handled) compute a scaled amount, and
     only when a raw cents value is actually present; any other case (cents
     missing, Shopify.formatMoney unavailable) falls back to the per-unit
     string rather than showing something blank or wrong. */
  function scaledPrice(cents, perUnitFormatted, qty) {
    if (qty <= 1) return perUnitFormatted;
    return formatMoney(cents * qty) || perUnitFormatted;
  }

  function renderPurchase() {
    var entry = variantsById[state.variantId];
    var pricing = entry && entry.pricing;
    if (!pricing) return;

    var qty = currentQuantity();
    var oncePriceText = scaledPrice(pricing.priceCents, pricing.price, qty);

    setText('[data-wep-out="once-price"]', oncePriceText);
    setText('[data-wep-out="once-serving"]', pricing.perServing ? '(' + pricing.perServing + '/serving)' : '');

    var plan = findPlan(pricing, state.planId);

    /* FIX (subscription-state audit, F1): a variant with no valid selling-
       plan allocation at all makes findPlan() return null (its own
       !pricing.plans.length guard). The previously-selected mode/planId
       may belong to a DIFFERENT variant in that case, so both are reset to
       a safe one-time state here - getPurchaseState() must never hand
       assets/we-pdp-cart.js a planId that doesn't apply to the currently
       resolved variant, which is exactly what could otherwise let Add to
       Cart submit {id: <this variant>, selling_plan: <a different
       variant's plan id>}. When a plan DOES exist, the pre-existing
       fallback below still applies: findPlan() falls back to the variant's
       first plan when the previously-selected plan id doesn't exist for
       this variant (e.g. the shopper had "3 Months" selected, then
       switched to a variant that doesn't offer it) - that fallback must
       correct state.planId itself, not just what gets displayed, and keep
       the <select> in sync so what's shown always matches what would
       actually be submitted. */
    if (!plan) {
      state.mode = 'once';
      state.planId = null;
    } else if (String(plan.id) !== String(state.planId)) {
      state.planId = plan.id;
      var freqSelect = qs('[data-wep-freq]');
      if (freqSelect) freqSelect.value = String(plan.id);
    }

    /* FIX (F2): the Subscribe card's markup now always exists whenever the
       product has selling plans at all (see we-pdp-buybox.liquid), so this
       is the one place, per render, that shows/hides it for the CURRENTLY
       resolved variant - native [hidden], same "reserve no space"
       mechanism already used elsewhere in this project, never a CSS
       display override on the card itself. */
    var subCard = qs('[data-wep-mode-card="sub"]');
    if (subCard) subCard.hidden = !plan;

    var isSub = state.mode === 'sub' && !!plan;
    var subPriceText = oncePriceText;
    var subOrigText = oncePriceText;

    if (plan) {
      subPriceText = scaledPrice(plan.priceCents, plan.price, qty);
      subOrigText = scaledPrice(plan.compareAtCents, plan.compareAt, qty);

      /* Savings scales the same way - "SAVE $11.34" at quantity 1 must
         become the total saved across all units bought, not the per-unit
         figure restated. Computed from the same two cents values already
         used for subPriceText/subOrigText, not a third source. */
      var savingsText = plan.savings;
      if (qty > 1 && typeof plan.priceCents === 'number' && typeof plan.compareAtCents === 'number') {
        savingsText = formatMoney((plan.compareAtCents - plan.priceCents) * qty) || plan.savings;
      }

      setText('[data-wep-out="sub-percent"]', String(plan.percent));
      setText('[data-wep-out="perk-percent"]', String(plan.percent));
      setText('[data-wep-out="save-pill"]', 'SAVE ' + savingsText);
      setText('[data-wep-out="sub-orig"]', subOrigText);
      setText('[data-wep-out="sub-price"]', subPriceText);
      setText('[data-wep-out="sub-serving"]', plan.perServing ? plan.perServing + '/serving' : '');
    } else {
      /* FIX (F1): blank every subscription-specific output rather than
         leaving a previous variant's numbers frozen on screen - the card
         is hidden above too, but this also keeps any other surface that
         might read these same data-wep-out targets from showing stale
         text the moment the card is unhidden again. */
      setText('[data-wep-out="sub-percent"]', '');
      setText('[data-wep-out="perk-percent"]', '');
      setText('[data-wep-out="save-pill"]', '');
      setText('[data-wep-out="sub-orig"]', '');
      setText('[data-wep-out="sub-price"]', '');
      setText('[data-wep-out="sub-serving"]', '');
    }

    var modes = qs('[data-wep-modes]');
    if (modes) {
      modes.dataset.wepSelected = isSub ? 'sub' : 'once';

      /* FIX (F1): mirrors exactly what selectMode()'s click handler already
         does to these same two attributes/class - needed here too now that
         renderPurchase() can itself force mode back to 'once' (variant
         switch, no click involved), which previously only ever happened
         from a direct mode-button click. Redundant, not conflicting, for
         every case that was already correct (a real click already sets
         these before calling renderPurchase()). */
      qsa('[data-wep-mode-card]', modes).forEach(function (card) {
        card.classList.toggle('we-pdp__mode-card--active', card.dataset.wepModeCard === (isSub ? 'sub' : 'once'));
      });
      qsa('[data-wep-mode]', modes).forEach(function (btn) {
        btn.setAttribute('aria-pressed', String(btn.dataset.wepMode === (isSub ? 'sub' : 'once')));
      });
    }

    /* UX fix: the delivery-frequency <select> lives inside the Subscribe
       card, but a native <select> is still focusable/interactive even
       while its containing card is visually inactive - disable it
       outright whenever One Time is the active mode, so a shopper can't
       change (or tab into) the subscription frequency while they've
       chosen not to subscribe. Re-enabled the moment isSub is true again
       (Subscribe re-selected, or a variant switch restores a valid plan).
       Uses the native disabled property, not a CSS-only affordance, and
       runs every renderPurchase() call - including refresh() on first
       load - so this is correct from the very first paint, not only
       after a click. Never touches state.mode/state.planId or the
       resolved plan itself. */
    var freqSelectEl = qs('[data-wep-freq]');
    if (freqSelectEl) freqSelectEl.disabled = !isSub;

    /* The one "what does the shopper currently pay" figure - subscription
       price when a plan is actually resolved and selected, one-time price
       otherwise. Named once and reused for every surface that mirrors it
       (sticky bar, testimonials mini-card) rather than repeating the same
       ternary at each call site. */
    var displayPriceText = isSub && plan ? subPriceText : oncePriceText;

    /* Sticky bar - mirrors the main buy-box's resolved state, same targets
       we-pdp-sticky-bar.liquid already renders (see its own header comment;
       the design's own "/mo" span it never uses is likewise omitted). */
    setText('[data-wep-out="sb-mode"]', isSub ? 'Subscribe & Save' : 'Buy one time');
    setText('[data-wep-out="sb-price"]', displayPriceText);
    setText('[data-wep-out="sb-orig"]', isSub && plan ? subOrigText : oncePriceText);

    /* Testimonials mini-card (we-pdp-testimonials.liquid) - the card
       represents THIS product/the currently selected variant (confirmed by
       that section's own header comment: it already sources name/image
       from `product` directly rather than the metaobject's own product
       reference, specifically to stay in sync with the live product), so
       its price is the same resolved-variant figure as everywhere else on
       the page - not a second price for a "different" product. Quantity-
       aware for the same reason sb-price is: displayPriceText already
       comes from oncePriceText/subPriceText, both already scaled by
       currentQuantity() above.

       qsa, not setText: unlike every other data-wep-out target in this
       file, this one repeats once per testimonial card (up to ten on this
       product), all showing the same resolved product/variant - so every
       match needs the same text, not just the first. */
    qsa('[data-wep-out="vos-price"]').forEach(function (el) {
      el.textContent = displayPriceText;
    });
    if (entry && entry.options) {
      /* Chips are keyed by option POSITION, not by this product's own option
         names: we-pdp-sticky-bar.liquid renders one chip per real option group
         from product.options_with_values, so a product with fewer than three
         options simply has no slot for the missing positions and setText
         no-ops on them. Same single sync path as before - only the slot
         names changed. */
      for (var oi = 0; oi < 3; oi++) {
        setText('[data-wep-out="sb-opt-' + (oi + 1) + '"]', entry.options[oi] || '');
      }
    }

    ['sb-orig'].forEach(function (name) {
      var el = qs('[data-wep-out="' + name + '"]');
      if (el) el.style.display = isSub ? '' : 'none';
    });
  }

  /* ------------------------------------------------------------------------
     assets/we-pdp-variants.js dispatches this on every real variant change
     (detail.variant is the same object [data-wep-variant-index] parsed,
     now including the "pricing" key). Kept a listener rather than a direct
     function call for the same reason we-pdp.js's old pricing bridge was:
     zero compile-time dependency on Variants existing.
     ------------------------------------------------------------------------ */
  function onVariantChange(event) {
    var variant = event && event.detail && event.detail.variant;
    if (!variant) return;
    state.variantId = variant.id;
    renderPurchase();
  }

  /* assets/we-pdp-cart.js dispatches this whenever its own quantity value
     actually changes (+/- buttons, typing, or a re-clamp against a newly
     selected variant's quantity_rule) - same "listen, don't resolve"
     relationship as onVariantChange above. */
  function onQuantityChange() {
    renderPurchase();
  }

  function selectMode(button) {
    var group = button.closest('[data-wep-modes]');
    if (!group) return;

    qsa('[data-wep-mode]', group).forEach(function (btn) {
      btn.setAttribute('aria-pressed', String(btn === button));
    });

    /* The selected state lives on the wrapping card so the border and
       background cover the whole card, benefits panel included. */
    var selectedCard = button.closest('[data-wep-mode-card]');
    qsa('[data-wep-mode-card]', group).forEach(function (card) {
      card.classList.toggle('we-pdp__mode-card--active', card === selectedCard);
    });

    state.mode = button.dataset.wepMode;
    renderPurchase();
  }

  function onFrequencyChange(select) {
    state.planId = select.value;
    renderPurchase();
  }

  /* ------------------------------------------------------------------------
     Delegated events - bound once
     ------------------------------------------------------------------------ */
  function onClick(event) {
    var target = event.target;
    if (!target || !target.closest) return;

    var mode = target.closest('[data-wep-mode]');
    if (mode) {
      selectMode(mode);
      return;
    }
  }

  function onChange(event) {
    var freq = event.target.closest && event.target.closest('[data-wep-freq]');
    if (freq) onFrequencyChange(freq);
  }

  /* ------------------------------------------------------------------------
     Init / refresh
     ------------------------------------------------------------------------ */
  function refresh() {
    loadVariants();
    state.variantId = initialVariantId();
    state.mode = initialMode();
    state.planId = initialPlanId();
    renderPurchase();
  }

  /* ------------------------------------------------------------------------
     Purchase-state API
     ------------------------------------------------------------------------
     assets/we-pdp-cart.js's single read of "what is currently selected" -
     not a second resolver, just exposing the state this file already
     tracks (renderPurchase() above keeps state.planId corrected to always
     be valid for state.variantId, so callers never need to re-validate
     it). Returns a fresh plain object each call so a caller can't mutate
     this module's internal state by holding a reference to it.
     ------------------------------------------------------------------------ */
  function getPurchaseState() {
    var entry = variantsById[state.variantId];
    return {
      variantId: state.variantId,
      available: entry ? entry.available : null,
      qty: entry ? entry.qty : null,
      mode: state.mode,
      planId: state.mode === 'sub' ? state.planId : null
    };
  }

  document.addEventListener('click', onClick);
  document.addEventListener('change', onChange);
  document.addEventListener('wep:variant-change', onVariantChange);
  document.addEventListener('wep:quantity-change', onQuantityChange);
  document.addEventListener('shopify:section:load', refresh);

  window.WePdpPricing = { bound: true, refresh: refresh, getPurchaseState: getPurchaseState };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', refresh);
  } else {
    refresh();
  }
})();
